import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BtSparkline from '../bt-sparkline/bt-sparkline';
import { SupplierSummary } from '../../services/service-evaluations';
import { EvaluationLimits } from '../../util/evaluationContract';
import { formatScore, trendDelta } from '../../util/evaluationFormat';
import { trendTone } from '../../util/evaluationStatusMeta';
import { formatStockholmDay, formatTrendLabel } from './reviewFormat';
import ReadOnlyStars from './ReadOnlyStars';
import styles from './page-supplier-evaluations.module.scss';

type Props = {
  summary: SupplierSummary;
};

const PILL_ICON = { fontSize: 14 };

const Trend: FC<{ points: SupplierSummary['trend'] }> = ({ points }) => {
  const [t] = useTranslation('evaluations');
  const change = trendDelta(points);
  const title = <p className={styles.label}>{t('review.trend.title', 'Betyg över tid')}</p>;

  if (!change) {
    return (
      <>
        {title}
        <span className={`${styles.muted} ${styles.small}`}>
          {t('review.trend.tooFew', 'Trend visas från tre svar.')}
        </span>
      </>
    );
  }

  const tone = trendTone(change.delta);

  return (
    <>
      <div className={styles.trendHeader}>
        {title}
        <span
          className={styles.trendPill}
          style={{ background: tone.backgroundColor, color: tone.color }}
        >
          {change.delta < 0 ? (
            <TrendingDownIcon style={PILL_ICON} aria-hidden />
          ) : (
            <TrendingUpIcon style={PILL_ICON} aria-hidden />
          )}
          {formatTrendLabel(change)}
        </span>
      </div>
      <BtSparkline
        values={points.map(point => point.score)}
        min={EvaluationLimits.ScaleMin}
        max={EvaluationLimits.ScaleMax}
        gridLines={[1, 3, 5]}
        startLabel={formatStockholmDay(points[0].submittedAt)}
        endLabel={formatStockholmDay(points[points.length - 1].submittedAt)}
        width={640}
        height={72}
        fluid
      />
    </>
  );
};

const ScoreOverview: FC<Props> = ({ summary }) => {
  const [t] = useTranslation('evaluations');
  const { supplier, score, evaluationCount, evaluatedProjectsCount, trend } = summary;
  const rated = score !== null && evaluationCount > 0;

  return (
    <>
      <div className={styles.heroHeader}>
        <h2 className={styles.entityTitle}>{supplier.name}</h2>
        {rated && (
          <span className={styles.muted}>
            {t('supplierScore.answerCount', {
              count: evaluationCount,
              defaultValue_one: '{{count}} svar',
              defaultValue_other: '{{count}} svar',
            })}
            {' · '}
            {t('review.score.projectCount', {
              count: evaluatedProjectsCount,
              defaultValue_one: '{{count}} projekt',
              defaultValue_other: '{{count}} projekt',
            })}
          </span>
        )}
      </div>
      <div className={`${styles.hero} ${evaluationCount > 0 ? '' : styles.heroSingle}`}>
        <section className={styles.score}>
          <p className={styles.label}>{t('review.score.title', 'Totalbetyg')}</p>
          {rated ? (
            <div className={styles.scoreRow}>
              <span className={styles.scoreNumber}>{formatScore(score)}</span>
              <ReadOnlyStars rating={score} size={18} />
            </div>
          ) : (
            <span className={styles.muted}>{t('supplierScore.notRated', 'Inte betygsatt')}</span>
          )}
          <span className={`${styles.muted} ${styles.small} ${styles.scoreExplainer}`}>
            {t('review.score.explainer', 'Snittet av varje svars medelvärde. Vet ej räknas inte.')}
          </span>
        </section>
        {evaluationCount > 0 && (
          <section className={styles.trend}>
            <Trend points={trend} />
          </section>
        )}
      </div>
    </>
  );
};

export default ScoreOverview;
